import React from 'react';
import { Mail, Send, Clock, AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Admin — Notifications tab. Expiration emails log + manual trigger + warnings.
 * Extracted from AdminPanel.jsx on 29/05/2026 (code review refactor).
 */
export default function NotificationsTab({ licenses, notifLog, checkingNotifs, onTriggerCheck }) {
  const noContact = licenses.filter((l) => l.is_active && !l.contact_email);
  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2">
          <Mail className="w-5 h-5 text-foreground" /> Notifications d&apos;expiration
        </h2>
        <button
          onClick={onTriggerCheck} disabled={checkingNotifs}
          className="flex items-center gap-2 bg-accent text-white px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-accent-hover transition-colors disabled:opacity-50"
          data-testid="trigger-notif-check"
        >
          <Send className={`w-4 h-4 ${checkingNotifs ? 'animate-pulse' : ''}`} />
          {checkingNotifs ? 'Vérification...' : 'Vérifier maintenant'}
        </button>
      </div>

      <NotificationsHowItWorks />

      {noContact.length > 0 && <NoContactWarning licenses={noContact} />}

      <NotificationsLog notifLog={notifLog} />
    </>
  );
}

function NotificationsHowItWorks() {
  return (
    <div className="bg-accent/10 rounded-xl p-4 border border-accent/40">
      <h3 className="text-sm font-semibold text-blue-800 mb-2">Fonctionnement</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-accent">
        <HowItWorksItem icon={Clock} color="warn" title="J-30 : Premier avis" body="Email d'information envoyé au contact primaire 30 jours avant expiration" />
        <HowItWorksItem icon={AlertTriangle} color="danger" title="J-7 : Relance urgente" body="Email urgent envoyé 7 jours avant expiration" />
        <HowItWorksItem icon={RefreshCw} color="accent" title="Automatique" body="Vérification toutes les 6h. Bouton ci-dessus pour forcer une vérification." />
      </div>
    </div>
  );
}

function HowItWorksItem({ icon: Icon, color, title, body }) {
  const bg = { warn: 'bg-warn/15', danger: 'bg-danger/15', accent: 'bg-accent/15' }[color];
  const textColor = { warn: 'text-warn', danger: 'text-danger', accent: 'text-accent' }[color];
  return (
    <div className="flex items-start gap-2">
      <div className={`w-5 h-5 rounded-full ${bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Icon className={`w-3 h-3 ${textColor}`} />
      </div>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-accent">{body}</p>
      </div>
    </div>
  );
}

function NoContactWarning({ licenses }) {
  return (
    <div className="bg-warn/10 rounded-xl p-4 border border-warn/40" data-testid="no-contact-warning">
      <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4" /> {licenses.length} licence(s) active(s) sans contact email
      </h3>
      <div className="flex flex-wrap gap-2">
        {licenses.map((l) => (
          <span key={l.id} className="ph-no-capture text-xs bg-ink-800 border border-warn/40 rounded-md px-2 py-1 text-warn font-mono">
            {l.license_key} — {l.holder_name}
          </span>
        ))}
      </div>
      <p className="text-[10px] text-warn mt-2">Ces licences ne recevront pas de notifications. Ajoutez un contact email dans l&apos;onglet Licences.</p>
    </div>
  );
}

function NotificationsLog({ notifLog }) {
  return (
    <div className="bg-ink-800 rounded-xl border border-ink-600 shadow-sm">
      <div className="px-4 py-3 border-b border-ink-600">
        <h3 className="text-sm font-semibold text-slate-200">Historique des notifications</h3>
      </div>
      {notifLog.length === 0 ? (
        <div className="px-4 py-8 text-center text-slate-500 text-sm">
          Aucune notification envoyée pour le moment.
        </div>
      ) : (
        <div className="divide-y divide-gray-100" data-testid="notif-log">
          {notifLog.map((n) => (
            <NotifLogRow key={`${n.license_key}-${n.type}-${n.sent_at}`} n={n} />
          ))}
        </div>
      )}
    </div>
  );
}

function NotifLogRow({ n }) {
  return (
    <div className="px-4 py-3 flex items-center gap-4">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${n.type === '7d' ? 'bg-danger/15' : 'bg-warn/15'}`}>
        {n.type === '7d' ? <AlertTriangle className="w-4 h-4 text-danger" /> : <Clock className="w-4 h-4 text-warn" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-200 font-medium">
          {n.type === '7d' ? 'Relance urgente' : 'Premier avis'} — <span className="ph-no-capture font-mono text-xs">{n.license_key}</span>
        </p>
        <p className="text-xs text-slate-500">Envoyé à {n.to_email} — {n.days_left} jours restants</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs text-slate-500">
          {new Date(n.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </p>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${n.success ? 'bg-money/10 text-money' : 'bg-danger/10 text-danger'}`}>
          {n.success ? 'Envoyé' : 'Échec'}
        </span>
      </div>
    </div>
  );
}
